import { CATEGORICAL, CATEGORICAL_AUTO_SLOTS, CATEGORICAL_SLOT_COUNT, DEFAULT_CATEGORICAL, INK, NEUTRAL } from "../theme/tokens";
import { type Job, NurseFullRole, NurseFullRoleShort } from "./models";
import { hashString } from "./utilFunctions";

/** One categorical colour in its three shades — see `CATEGORICAL` in theme/tokens. */
export type CategoricalColor = {
	/** `null` for the neutral colour, which is no slot. */
	slot: number | null;
	dot: string;
	fill: string;
	ink: string;
};

/** Colour for things that have no categorical identity (an undefined role, a job outside the department). */
export const NEUTRAL_CATEGORICAL_COLOR: CategoricalColor = { slot: null, dot: NEUTRAL[450], fill: NEUTRAL[200], ink: INK.MUTED };

export const isCategoricalSlot = (slot: number): boolean => Number.isInteger(slot) && slot >= 0 && slot < CATEGORICAL_SLOT_COUNT;

export const categoricalColor = (slot: number): CategoricalColor => {
	if (!isCategoricalSlot(slot)) throw new Error(`Categorical slot ${slot} is out of range 0..${CATEGORICAL_SLOT_COUNT - 1}`);
	return { slot, dot: CATEGORICAL.DOT[slot], fill: CATEGORICAL.FILL[slot], ink: CATEGORICAL.INK[slot] };
};

const ALL_SLOTS = Array.from({ length: CATEGORICAL_SLOT_COUNT }, (_, slot) => slot);
const AUTO_SLOTS = ALL_SLOTS.slice(0, CATEGORICAL_AUTO_SLOTS);

/** The colour for the `index`-th item of an ordered list — cycles through the automatic slots only. */
export const categoricalColorAt = (index: number): CategoricalColor => categoricalColor(index % CATEGORICAL_AUTO_SLOTS);

/**
 * Colour for a free-form label that belongs to no list it could be ranked in (a person's avatar, a demo unit
 * name). Stable per label but two labels CAN share a colour — anything that has a list (jobs, roles,
 * departments) uses its position instead.
 */
export const freeLabelColor = (label: string): CategoricalColor => categoricalColorAt(Math.abs(hashString(label)));

/**
 * Slot per role for saved pin and group bookkeeping. Default colours come from `DEFAULT_ROLE_INDEX`;
 * roles sharing a slot here may have different default colours. `null` = no identity colour.
 */
const ROLE_TO_SLOT: Record<NurseFullRole, number | null> = {
	[NurseFullRole.RN]: 0,
	[NurseFullRole.CNA]: 1,
	[NurseFullRole.CG]: 1,
	[NurseFullRole.CA]: 1,
	[NurseFullRole.LPN]: 2,
	[NurseFullRole.LVN]: 2,
	[NurseFullRole.CMA]: 3,
	[NurseFullRole.MT]: 4,
	[NurseFullRole.NP]: 5,
	[NurseFullRole.RNA]: 6,
	[NurseFullRole.RLPN]: 6,
	[NurseFullRole.NAS]: 7,
	[NurseFullRole.MDS]: 8,
	[NurseFullRole.RT]: 9,
	[NurseFullRole.HA]: 10,
	[NurseFullRole.RA]: 11,
	[NurseFullRole.CK]: 8,
	[NurseFullRole.CKA]: 8,
	[NurseFullRole.DA]: 9,
	[NurseFullRole.RD]: 10,
	[NurseFullRole.LNA]: 10,
	[NurseFullRole.HK]: 11,
	[NurseFullRole.ACT]: 5,
	[NurseFullRole.REC]: 7,
	[NurseFullRole.UC]: 3,
	[NurseFullRole.LCSW]: 6,
	[NurseFullRole.tech]: 4,
	[NurseFullRole.CRNA]: 5,
	[NurseFullRole.MD]: 0,
	[NurseFullRole.DO]: 1,
	[NurseFullRole.ACN]: 3,
	[NurseFullRole.PO]: 4,
	[NurseFullRole.UDF]: 11,
};

/** Fixed role indices for default colours. Roles outside this list have a name-derived default colour. */
const DEFAULT_ROLE_INDEX: Partial<Record<NurseFullRole, number>> = {
	[NurseFullRole.CNA]: 0,
	[NurseFullRole.RNA]: 1,
	[NurseFullRole.RN]: 2,
	[NurseFullRole.CG]: 0,
	[NurseFullRole.CMA]: 3,
	[NurseFullRole.LVN]: 4,
	[NurseFullRole.LPN]: 4,
	[NurseFullRole.MT]: 5,
	[NurseFullRole.NAS]: 6,
	[NurseFullRole.RA]: 7,
	[NurseFullRole.DA]: 8,
	[NurseFullRole.CK]: 9,
	[NurseFullRole.RD]: 10,
	[NurseFullRole.tech]: 11,
	[NurseFullRole.CRNA]: 12,
	[NurseFullRole.MD]: 0,
	[NurseFullRole.DO]: 1,
	[NurseFullRole.NP]: 2,
	[NurseFullRole.ACN]: 3,
	[NurseFullRole.PO]: 4,
};

const DEFAULT_ROLE_GROUP_INDEX: Readonly<Record<string, number>> = {
	Nurse: 8,
	CNA: 7,
	"Restorative aid": 12,
};

const DEFAULT_NAME_HUE_STEP = 137.508;
const DEFAULT_PRIMARY_SATURATION = 80;
const DEFAULT_PRIMARY_LIGHTNESS = 75;
const DEFAULT_FILL_SATURATION = 70;
const DEFAULT_FILL_LIGHTNESS = 87;
const DEFAULT_INK_SATURATION = 58;
const DEFAULT_INK_LIGHTNESS = 42;

const hslToHex = (hue: number, saturation: number, lightness: number): string => {
	const s = saturation / 100;
	const l = lightness / 100;
	const a = s * Math.min(l, 1 - l);
	const channel = (n: number): string => {
		const k = (n + hue / 30) % 12;
		return Math.round(255 * (l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1)))
			.toString(16)
			.padStart(2, "0");
	};
	return `#${channel(0)}${channel(8)}${channel(4)}`.toUpperCase();
};

const hueOf = (hex: string): number => {
	const [r, g, b] = [1, 3, 5].map((start) => Number.parseInt(hex.slice(start, start + 2), 16) / 255);
	const max = Math.max(r, g, b);
	const delta = max - Math.min(r, g, b);
	if (delta === 0) return 0;
	const sector = max === r ? ((g - b) / delta) % 6 : max === g ? (b - r) / delta + 2 : (r - g) / delta + 4;
	return (Math.round(sector * 60) + 360) % 360;
};

const defaultFixedColor = (index: number, slot: number): CategoricalColor => {
	const primary = DEFAULT_CATEGORICAL.PRIMARY[index];
	return {
		slot,
		dot: primary,
		fill: DEFAULT_CATEGORICAL.SECONDARY[index],
		ink: hslToHex(hueOf(primary), DEFAULT_INK_SATURATION, DEFAULT_INK_LIGHTNESS),
	};
};

const defaultNameColor = (name: string, slot: number): CategoricalColor => {
	const hue = (Math.abs(hashString(name)) * DEFAULT_NAME_HUE_STEP) % 360;
	return {
		slot,
		dot: hslToHex(hue, DEFAULT_PRIMARY_SATURATION, DEFAULT_PRIMARY_LIGHTNESS),
		fill: hslToHex(hue, DEFAULT_FILL_SATURATION, DEFAULT_FILL_LIGHTNESS),
		ink: hslToHex(hue, DEFAULT_INK_SATURATION, DEFAULT_INK_LIGHTNESS),
	};
};

const defaultRoleColor = (role: NurseFullRole, slot: number): CategoricalColor => {
	const index = DEFAULT_ROLE_INDEX[role];
	// DEFAULT: roles without a fixed index use a stable color derived from their full name.
	return index === undefined ? defaultNameColor(role, slot) : defaultFixedColor(index, slot);
};

const defaultRoleGroupColor = (name: string, slot: number): CategoricalColor => {
	const index = DEFAULT_ROLE_GROUP_INDEX[name];
	// DEFAULT: groups without a fixed index use the color derived from their label.
	return index === undefined ? defaultNameColor(name, slot) : defaultFixedColor(index, slot);
};

/** Short role code → selected palette slot; unpinned roles keep their default colour. */
export type RoleColorPins = Readonly<Record<string, number>>;
export type RoleColorResolver = (role: NurseFullRole) => CategoricalColor;
export type RoleGroupColorResolver = (roleGroup: string, roleGroups: Readonly<Record<string, readonly string[]>>) => CategoricalColor;

const roleSlot = (role: NurseFullRole, pins: RoleColorPins): number | null | undefined => {
	const code = NurseFullRoleShort[role];
	// DEFAULT: an unpinned role keeps its assigned bookkeeping slot.
	return pins[code] ?? ROLE_TO_SLOT[role];
};

export const buildRoleColorResolver =
	(pins: RoleColorPins): RoleColorResolver =>
	(role) => {
		const slot = roleSlot(role, pins);
		if (slot === null || slot === undefined) return NEUTRAL_CATEGORICAL_COLOR;
		return pins[NurseFullRoleShort[role]] === undefined ? defaultRoleColor(role, slot) : categoricalColor(slot);
	};

export const roleColor = (role: NurseFullRole): CategoricalColor => {
	const slot = ROLE_TO_SLOT[role];
	// DEFAULT: `undefined` is a role string off the wire that isn't a known role (typed as one by a cast upstream);
	// it gets the no-identity colour rather than crashing the row that shows it.
	if (slot === null || slot === undefined) return NEUTRAL_CATEGORICAL_COLOR;
	return defaultRoleColor(role, slot);
};

// The cast only lets an arbitrary code index the enum; a code that isn't a role reads back `undefined`.
const shortRoleToFullRole = (shortRole: string): NurseFullRole | undefined => NurseFullRole[shortRole as keyof typeof NurseFullRole];

/** A group inherits a role colour only when all its known roles actually look alike. */
const singleRoleColor = (members: readonly string[], resolveRole: RoleColorResolver): CategoricalColor | undefined => {
	const colors = members
		.map(shortRoleToFullRole)
		.filter((role) => role !== undefined)
		.map(resolveRole);
	const first = colors[0];
	return first !== undefined && colors.every((color) => color.dot === first.dot) ? first : undefined;
};

/**
 * A role group's default colour comes from its name, as it did before Appearance. A direct role uses
 * that role's colour; a saved role swatch also colours a group when all its roles resolve to that swatch.
 */
export const buildRoleGroupColorResolver = (pins: RoleColorPins): RoleGroupColorResolver => {
	const resolveRole = buildRoleColorResolver(pins);
	return (roleGroup, roleGroups) => {
		const directRole = shortRoleToFullRole(roleGroup);
		if (directRole) return resolveRole(directRole);
		const members = roleGroups[roleGroup];
		// DEFAULT: a group name the department no longer defines (stale settings, a removed group) has no
		// identity to show; neutral makes that visible instead of borrowing a live group's colour.
		if (members === undefined) return NEUTRAL_CATEGORICAL_COLOR;
		const ownColor = singleRoleColor(members, resolveRole);
		if (members.some((code) => pins[code] !== undefined) && ownColor !== undefined) return ownColor;
		// A group named after a role ("RN": ["RN"]) or made of one role holds that role's slot; the rest are mixed.
		const fixedSlotOf = (name: string): number | null | undefined => {
			const role = shortRoleToFullRole(name);
			return role ? resolveRole(role).slot : singleRoleColor(roleGroups[name], resolveRole)?.slot;
		};
		const groupNames = Object.keys(roleGroups);
		const singleSlots = new Set(groupNames.map(fixedSlotOf));
		const mixedGroups = groupNames.filter((name) => fixedSlotOf(name) === undefined);
		const free = ALL_SLOTS.filter((slot) => !singleSlots.has(slot));
		const cycle = free.length > 0 ? free : AUTO_SLOTS;
		const groupIndex = mixedGroups.indexOf(roleGroup);
		const slot = groupIndex < 0 ? ownColor?.slot : cycle[groupIndex % cycle.length];
		if (slot === undefined || slot === null) throw new Error(`No color slot assigned to role group ${roleGroup}`);
		return defaultRoleGroupColor(roleGroup, slot);
	};
};

/** Default role-group colours for callers outside a department context. */
export const roleGroupColor = buildRoleGroupColorResolver({});

/** `jobId → slot` the user (or their department default) pinned. */
export type JobColorPins = Readonly<Record<string, number>>;

export type JobColorResolver = (jobId: string) => CategoricalColor;

/** Nurse and Aide use the RN/CNA default colours; saved role choices do not change job colours. */
const JOB_ANCHOR_ROLE: Readonly<Record<string, NurseFullRole>> = {
	nurse: NurseFullRole.RN,
	aide: NurseFullRole.CNA,
};

const NO_RESERVED_SLOTS: ReadonlySet<number> = new Set();

const bySortOrder = (a: Job, b: Job): number => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name);

/** Hands out `jobs` the given slots in order, wrapping through the ordered slots once they run out. */
const dealSlots = (jobs: readonly Job[], available: readonly number[], into: Map<string, number>): void => {
	// Every slot is taken: the rest wrap through the automatic ones — the label tells them apart.
	const cycle = available.length > 0 ? available : AUTO_SLOTS;
	for (const [index, job] of jobs.entries()) into.set(job.id, cycle[index % cycle.length]);
};

/**
 * The bookkeeping slot each job takes with no pins: Nurse/Aide their fixed slots, then the rest in job
 * order. Unpinned jobs still display their default colours independently of these slots.
 */
const automaticSlots = (ranked: readonly Job[], reserved: ReadonlySet<number>): Map<string, number> => {
	const slots = new Map<string, number>();
	for (const job of ranked) {
		const anchor = JOB_ANCHOR_ROLE[job.name.toLowerCase()];
		const anchorSlot = anchor ? ROLE_TO_SLOT[anchor] : null;
		if (anchorSlot !== null && anchorSlot !== undefined) slots.set(job.id, anchorSlot);
	}
	const taken = new Set([...slots.values(), ...reserved]);
	dealSlots(
		ranked.filter((job) => !slots.has(job.id)),
		ALL_SLOTS.filter((slot) => !taken.has(slot)),
		slots,
	);
	return slots;
};

/**
 * Pins win; every other job keeps its bookkeeping slot unless a pin took it. A displaced job takes
 * the next unused slot; its displayed default colour stays unchanged.
 */
const assignGroupSlots = (ranked: readonly Job[], pins: JobColorPins, reserved: ReadonlySet<number>): Map<string, number> => {
	const automatic = automaticSlots(ranked, reserved);
	const slots = new Map<string, number>();
	for (const job of ranked) {
		const pinned = pins[job.id];
		if (pinned !== undefined) slots.set(job.id, pinned);
	}
	const pinnedSlots = new Set(slots.values());
	const displaced: Job[] = [];
	for (const job of ranked) {
		if (slots.has(job.id)) continue;
		const slot = automatic.get(job.id);
		if (slot === undefined || pinnedSlots.has(slot)) displaced.push(job);
		else slots.set(job.id, slot);
	}
	const used = new Set([...slots.values(), ...reserved]);
	dealSlots(
		displaced,
		ALL_SLOTS.filter((slot) => !used.has(slot)),
		slots,
	);
	return slots;
};

/**
 * Enabled jobs take bookkeeping slots before other catalog jobs, so catalog-only jobs cannot displace
 * an enabled job. Unpinned display colours remain stable regardless of the enabled set.
 */
const assignJobSlots = (jobs: readonly Job[], enabledJobIds: readonly string[], pins: JobColorPins): Map<string, number> => {
	const enabled = new Set(enabledJobIds);
	const enabledSlots = assignGroupSlots(jobs.filter((job) => enabled.has(job.id)).sort(bySortOrder), pins, NO_RESERVED_SLOTS);
	const otherSlots = assignGroupSlots(jobs.filter((job) => !enabled.has(job.id)).sort(bySortOrder), pins, new Set(enabledSlots.values()));
	return new Map([...otherSlots, ...enabledSlots]);
};

/**
 * Saved pins use the selectable palette. Without a pin, Nurse/Aide use the RN/CNA default colours and
 * other jobs use their name-derived default colours. The slot assignment still tracks pinned conflicts
 * and keeps the same job stable across screens.
 */
export const buildJobColorResolver = ({
	jobs,
	enabledJobIds,
	pins,
}: {
	jobs: readonly Job[];
	enabledJobIds: readonly string[];
	pins: JobColorPins;
}): JobColorResolver => {
	const slots = assignJobSlots(jobs, enabledJobIds, pins);
	const colors = new Map<string, CategoricalColor>();
	for (const job of jobs) {
		const slot = slots.get(job.id);
		if (slot === undefined) throw new Error(`No color slot assigned to job ${job.id}`);
		if (pins[job.id] !== undefined) {
			colors.set(job.id, categoricalColor(slot));
			continue;
		}
		const anchor = JOB_ANCHOR_ROLE[job.name.toLowerCase()];
		// DEFAULT: jobs without an RN/CNA anchor use the color derived from their catalog name.
		colors.set(job.id, anchor === undefined ? defaultNameColor(job.name, slot) : defaultRoleColor(anchor, slot));
	}
	return (jobId: string) => {
		// DEFAULT: an id outside the department's jobs has no identity colour here.
		return colors.get(jobId) ?? NEUTRAL_CATEGORICAL_COLOR;
	};
};

/**
 * A shift's job colour, or `undefined` when it has no job or the job can't be resolved — so every view falls back
 * to the same role colour instead of one showing grey and another the role.
 */
export const shiftJobColor = (jobColor: JobColorResolver, jobId: string | undefined): CategoricalColor | undefined => {
	if (jobId === undefined) return undefined;
	const color = jobColor(jobId);
	return color.slot === null ? undefined : color;
};

/** Colour per department, by its position in the user's department list. */
export const departmentColor = (departmentId: string, orderedDepartmentIds: readonly string[]): CategoricalColor => {
	const index = orderedDepartmentIds.indexOf(departmentId);
	// DEFAULT: a department the user is no longer a member of (an old notification) has no place in their list.
	if (index < 0) return NEUTRAL_CATEGORICAL_COLOR;
	return categoricalColorAt(index);
};
